import { notification } from "antd";
import type { AxiosError, AxiosResponse } from "axios";
import { useLoginStore } from "features/login/store";
import { axiosWithoutToken, axiosWithToken } from "./axios";
import { type BuildUrlParams, buildUrl } from "./buildUrl";

export type ApiResponse<T> = {
	registros: T[];
	total_registros?: number;
};

export async function newRequest<T>({
	token = true,
	url,
	type,
	idpk,
	params,
	body,
	empresa_idpk,
	timeout,
	showErrorNotification = true,
}: {
	/** Se a requisição precisa de token. Default: true */
	token?: boolean;

	/** Endpoint. Ex: Cliente/Consultar */
	url: string;
	/** Tipo da requisição HTTP */
	type: "get" | "post" | "put" | "delete";

	/** Idpk para ser incluído na url */
	idpk?: number;
	/** Parâmetros. Se token = true, será enviado o idpk da empresa. */
	params?: BuildUrlParams;
	/** Body da requisição */
	body?: Record<string, unknown> | Record<string, unknown>[];

	empresa_idpk?: number;

	timeout?: number;
	showErrorNotification?: boolean;
}): Promise<T> {
	const renovandoToken = useLoginStore.getState().renovandoToken;

	// Espera pra sempre, quando renovar o token vai reiniciar a página e refazer as requisições
	if (renovandoToken) {
		await new Promise(() => {
			//
		});
	}

	try {
		const axios = token ? axiosWithToken : axiosWithoutToken;

		let paramsWithEmpresaIdpk: Optional<BuildUrlParams>;

		if (empresa_idpk) {
			paramsWithEmpresaIdpk = {
				...params,
				empresa_idpk: 5748,
			};
		} else {
			paramsWithEmpresaIdpk = token
				? { empresa_idpk: 5748, ...params } //useLoginStore.getState().empresaIdpk
				: params;
		}

		const urlWithParams = buildUrl(url, paramsWithEmpresaIdpk, idpk);

		const response = await axios[type](urlWithParams, body, { timeout });
		throwIfResponseIsNotValid(response, type);

		return response.data;
	} catch (e) {
		if (showErrorNotification) {
			notification.error({
				title: "Falha ao realizar requisição",
				description: e.message,
			});
		}
		throw e;
	}
}

function throwIfResponseIsNotValid(responseAxios: AxiosError<unknown> | AxiosResponse<unknown>, type: "get" | "post" | "put" | "delete"): void {
	let response = responseAxios as AxiosResponse;

	if (response?.data === undefined) {
		response = (responseAxios as AxiosError)?.response as AxiosResponse;
	}

	if (response?.data === undefined) {
		throw new Error("Conexão falhou ou servidor está indisponível.");
	}

	if (response?.data?.status === "erro" || response?.data?.status === "error" || response?.data?.statusCode === 400) {
		throw new Error(response?.data?.message || response?.data?.mensagem || response?.data?.mensagem_original || JSON.stringify(response.data));
	}

	// Ao realizar um post, pode ser que algum registro falhou ao ser inserido
	if (type !== "post") {
		return;
	}

	if (Array.isArray(response?.data?.registros)) {
		for (const registro of response.data.registros) {
			if ("status" in registro) {
				if (registro.status === "erro") {
					throw new Error(registro.message);
				}
			}
		}
	}
}
